import React, { FC, useEffect } from 'react';
import {
  BasicContainer,
  BodyText,
  BodyTextLarge,
  BodyTextSmall,
  DetailText,
  FlexCol,
  FlexDynamic,
  FlexRow,
  ImageButton,
  TextDynamic,
} from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { PaywallGetBillingInfoQuery } from '@minecraft.graphql-operations';
import { useTranslation } from '@blocs.i18n';
// Eventually the older credit card component will be removed, and we will want to move those here
// For now we can share this information with the older credit card component.
import { displayCardIcon, displayCardType } from '../CreditCard/CreditCard';
import { PAYMENT_TYPES } from '../enums/PaymentMethods';
import { PAYPAL } from '../constants';

export type PaymentMethodsProps = {
  wallet?: PaywallGetBillingInfoQuery['getBillingInfo'];
  selectedPaymentOption?: PaywallGetBillingInfoQuery['getBillingInfo'][0];
  onSelectPaymentOption: (billingInfo: PaywallGetBillingInfoQuery['getBillingInfo'][0]) => void;
  onAddPaymentMethod: () => void;
};

const CreditCardButton: FC<{
  card: PaywallGetBillingInfoQuery['getBillingInfo'][0];
  isSelected?: boolean;
  onSelect: (billingInfo: PaywallGetBillingInfoQuery['getBillingInfo'][0]) => void;
}> = ({ card, onSelect, isSelected }) => {
  const { t } = useTranslation();
  const cardIcon = displayCardIcon(card.cardType, card.paymentType);

  return (
    <FlexCol
      data-qa-id="payment-method-item"
      bgColor={isSelected ? 'card' : 'listItem'}
      customBorders={{
        bottom: { borderColor: 'default', borderSize: 0.06 },
        left: isSelected ? { borderColor: 'select', borderSize: 0.3 } : { borderColor: 'default', borderSize: 0.06 },
        top: { borderColor: 'default', borderSize: 0.06 },
        right: { borderColor: 'default', borderSize: 0.06 },
      }}
      borderRadius="md"
      w="full"
      className="cn_relative cn_atom_py-4"
    >
      <ImageButton
        type="button"
        className="cn_atom_pt-4 cn_atom_pb-4"
        onClick={() => onSelect(card)}
        aria-label={
          card.paymentType === PAYMENT_TYPES.PAYPAL
            ? PAYPAL
            : t('common:paywall.paymentMethods.cardLabel', { cardType: card.cardType, lastFour: card.lastFour })
        }
      >
        <FlexCol wrap="nowrap" ml="1">
          <FlexRow className="cn_absolute cn_uppercase" gap="1" top={0.5} right={0.5}>
            {card.isPrimaryPaymentMethod && (
              <BasicContainer
                padding={1}
                borderRadius="sm"
                bgColor="creditCardPriorityTag"
                customBorders={{
                  bottom: { borderColor: 'default', borderSize: 0.06 },
                  left: { borderColor: 'default', borderSize: 0.06 },
                  top: { borderColor: 'default', borderSize: 0.06 },
                  right: { borderColor: 'default', borderSize: 0.06 },
                }}
              >
                <DetailText data-qa-id="payment-primary-tag" as="p">
                  {t('common:paywall.paymentMethods.primary')}
                </DetailText>
              </BasicContainer>
            )}
            {card.isBackupPaymentMethod && (
              <BasicContainer
                padding={1}
                borderRadius="sm"
                bgColor="creditCardPriorityTag"
                customBorders={{
                  bottom: { borderColor: 'default', borderSize: 0.06 },
                  left: { borderColor: 'default', borderSize: 0.06 },
                  top: { borderColor: 'default', borderSize: 0.06 },
                  right: { borderColor: 'default', borderSize: 0.06 },
                }}
              >
                <DetailText data-qa-id="payment-backup-tag" as="p">
                  {t('common:paywall.paymentMethods.backup')}
                </DetailText>
              </BasicContainer>
            )}
          </FlexRow>
          <FlexRow gap="4" w="full" alignItems="center" wrap="nowrap">
            {cardIcon && <Icon height={3.4} name={cardIcon} />}
            <FlexCol>
              <BodyTextLarge fontWeight="semibold">{displayCardType(card.cardType, card.paymentType)}</BodyTextLarge>
              {card.lastFour && (
                <BodyTextSmall>
                  {t('common:paywall.paymentMethods.cardName', { lastFour: card.lastFour })}
                </BodyTextSmall>
              )}
            </FlexCol>
          </FlexRow>
        </FlexCol>
      </ImageButton>
    </FlexCol>
  );
};

const AddCardButton: FC<{ onSelect: () => void }> = ({ onSelect }) => {
  const { t } = useTranslation();

  return (
    <BasicContainer
      bgColor="listItem"
      w="full"
      className="cn_atom_py-4"
      borderPlacement="all"
      borderRadius="md"
      borderStyle="dotted"
      borderColor="select"
    >
      <ImageButton
        type="button"
        className="cn_atom_py-4"
        onClick={() => onSelect()}
        aria-label={t('common:paywall.paymentMethods.addCard')}
      >
        <FlexRow alignItems="center" className="cn_atom_p-4" gap="4">
          <Icon name="add" themeColor="primary" />
          <BodyText fontWeight="semibold" color="link">
            {t('common:paywall.paymentMethods.addCard')}
          </BodyText>
        </FlexRow>
      </ImageButton>
    </BasicContainer>
  );
};

export const PaymentMethods: FC<PaymentMethodsProps> = ({
  wallet,
  selectedPaymentOption,
  onSelectPaymentOption,
  onAddPaymentMethod,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!selectedPaymentOption && wallet?.length) {
      const defaultPaymentMethod = wallet.find((paymentMethod) => paymentMethod.isPrimaryPaymentMethod) ?? wallet[0];
      onSelectPaymentOption(defaultPaymentMethod);
    }
  }, [wallet, selectedPaymentOption, onSelectPaymentOption]);

  if (!wallet?.length) return null;

  return (
    <FlexDynamic w="full" data-testid="wallet-container">
      <FlexCol gap="4" w="full">
        <TextDynamic as="h2" fontWeight="medium" fontSize="bodyLarge" mdProps={{ fontSize: 'h2' }}>
          {t('common:paywall.paymentMethods.heading')}
        </TextDynamic>
        <FlexCol gap="1" w="full">
          {wallet?.map((paymentMethod) => (
            <CreditCardButton
              key={paymentMethod.id}
              card={paymentMethod}
              isSelected={selectedPaymentOption?.id === paymentMethod.id}
              onSelect={onSelectPaymentOption}
            />
          ))}
          <AddCardButton onSelect={onAddPaymentMethod} />
        </FlexCol>
      </FlexCol>
    </FlexDynamic>
  );
};
